import React, { useCallback, useEffect, useState } from 'react'
import Schedule from './components/Schedule/Schedule'
import RowOfDays from './components/RowOfDays/RowOfDays'
import TimelineScrubber from './components/TimelineScrubber/TimelineScrubber'
import { LoadingSkeleton } from './components/LoadingScheduleState/LoadingSkeleton'
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary'
import { StyledAppContainer } from './App.styled'
import {
    addFillerBlocks,
    Channel,
    EpgSchedule,
    EventDay,
    fetchEventDays,
    fetchChannels,
    fetchEpg,
    formatStartDate,
    getPlacement,
    shouldShowDetailedErrors,
    getTimeOfFirstEvent,
    Placement,
    sortChannels,
} from './helpers'
import { isToday, parseISO } from 'date-fns'
import { ThemeProvider } from '@emotion/react'
import { theme7news, theme7plus } from './helpers/theme'

const SERVICE_UNAVAILABLE_ERROR = 'Service unavailable. Please try again later.'

const formatDataErrorMessage = (error?: unknown) => {
    if (!shouldShowDetailedErrors()) {
        return SERVICE_UNAVAILABLE_ERROR
    }

    const details = error instanceof Error ? error.message : ''
    return details ? `${SERVICE_UNAVAILABLE_ERROR} ${details}` : SERVICE_UNAVAILABLE_ERROR
}

const getTheme = (placement: Placement) => {
    if (placement === '7plus') {
        return theme7plus
    } else {
        return theme7news
    }
}

function App() {
    const [errorMessage, setErrorMessage] = useState<string>('')
    const [startDate, setStartDate] = useState<string>('')
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [scrollBoxRef, setScrollBoxRef] = useState<React.RefObject<HTMLDivElement>>()

    const [channels, setChannels] = useState<Channel[]>([] as Channel[])
    const [epg, setEpg] = useState<EpgSchedule[]>([] as EpgSchedule[])
    const [eventDays, setEventDays] = useState<EventDay[]>([])
    const [firstEventOfDay, setFirstEventOfDay] = useState<string>('')
    const isReady = startDate !== '' && eventDays.length > 0

    const getDefaultEventDate = (days: EventDay[]) => {
        const today = days.find((day) => {
            return isToday(parseISO(formatStartDate(day.dateValue)))
        })

        if (today) {
            return today.dateValue
        }

        return days[0]?.dateValue || ''
    }

    useEffect(() => {
        let isMounted = true

        const loadEventDays = async () => {
            try {
                const dynamicEventDays = await fetchEventDays()
                if (!isMounted) {
                    return
                }

                if (dynamicEventDays.length === 0) {
                    setErrorMessage(
                        formatDataErrorMessage(new Error('No event days available from schedule data.')),
                    )
                    setEventDays([])
                    return
                }

                setErrorMessage('')
                setEventDays(dynamicEventDays)
                setStartDate((currentStartDate) => currentStartDate || getDefaultEventDate(dynamicEventDays))
            } catch (error) {
                if (!isMounted) {
                    return
                }
                setErrorMessage(formatDataErrorMessage(error))
                setEventDays([])
            }
        }

        loadEventDays()

        return () => {
            isMounted = false
        }
    }, [])

    const handleFetchResources = useCallback(async () => {
        if (!startDate) {
            return
        }

        setIsLoading(true)
        try {
            const epg = await fetchEpg(formatStartDate(startDate))
            const channels = sortChannels(await fetchChannels(formatStartDate(startDate)))
            const epgWithFillerBlocks = addFillerBlocks(epg, channels, formatStartDate(startDate))
            setEpg(epgWithFillerBlocks)
            if (epg.length > 0) {
                const firstEvent = getTimeOfFirstEvent(epg, startDate)
                setFirstEventOfDay(firstEvent)
            } else {
                setFirstEventOfDay('')
            }
            setChannels(channels)
            setErrorMessage('')
        } catch (error) {
            setErrorMessage(formatDataErrorMessage(error))
            setChannels([])
            setEpg([])
            setFirstEventOfDay('')
        } finally {
            setIsLoading(false)
        }
    }, [startDate])

    useEffect(() => {
        handleFetchResources()
    }, [handleFetchResources])

    return (
        <ThemeProvider theme={getTheme(getPlacement())}>
            <ErrorBoundary>
                <StyledAppContainer>
                    {errorMessage ? (
                        <div style={{ color: '#E6001E', padding: '16px 20px', fontWeight: 700 }}>
                            {errorMessage}
                        </div>
                    ) : !isReady ? (
                        <LoadingSkeleton />
                    ) : (
                        <>
                            <RowOfDays
                                selectedDate={startDate}
                                setSelectedDate={setStartDate}
                                eventDays={eventDays}
                            />
                            <TimelineScrubber
                                isLoading={isLoading}
                                scrollBoxRef={scrollBoxRef}
                                firstEventOfDay={firstEventOfDay}
                            />
                            <Schedule
                                startDate={startDate}
                                isLoading={isLoading}
                                setScrollBoxRef={setScrollBoxRef}
                                epg={epg}
                                channels={channels}
                            />
                        </>
                    )}
                </StyledAppContainer>
            </ErrorBoundary>
        </ThemeProvider>
    )
}

export default App
